import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import UpdatePaymentProfile from "service/paymentServices/UpdatePaymentProfile";
import DialogBackdrop from "./DialogBackdrop";
import Tooltip from "components/General/Tooltip";
import FormInputError from "components/General/FormInputError";
import MaskedExpirationDateInput from "components/General/Form/MaskedExpirationDateInput";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PaymentProfileConfirmationModel from "./PaymentProfileConfirmationModel";

const UpdateCCModal = ({
  modalState,
  setModalState,
  data,
  selectedPolicy,
  isUpdated,
  setIsUpdated,
  setToastDetail,
}) => {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const [disableSave, setDisableSave] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [title, setTitle] = useState(false);
  const [message, setMessage] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const isValidExpireDate = () => {
    //months should be b/n 1 and 12
    if (
      parseInt(getValues("expirationDate").substring(0, 2)) <= 0 ||
      parseInt(getValues("expirationDate").substring(0, 2)) > 12
    )
      return false;
    //expire year should be >= current year
    if (moment().year() > getValues("expirationDate")?.substring(3))
      return false;
    // if the same year months should be > current month
    if (moment().year() === getValues("expirationDate")?.substring(3)) {
      if (
        moment().month() + 1 >=
        parseInt(getValues("expirationDate").substring(0, 2))
      )
        return false;
    }
    return true;
  };

  async function onSubmit(result) {
    let primaryOwner = selectedPolicy[0]?.parties?.find(
      (x) => x.person?.taxId === user.identityNumber
    );
    const cardholderFullName = result?.firstName + result?.lastName;
    const ownerFullName =
      primaryOwner?.person?.firstName + primaryOwner?.person?.lastName;
    setIsLoading(true);
    // reset toast
    setToastDetail({
      type: undefined,
    });
    if (csrUser?.userId) {
      setTitle(t("CSRErrorMessage-title"));
      setMessage(t("CSRErrorMessage-message"));
      setIsLoading(false);
      setOpenDialog(true);
      setModalState(false);
      setDisableSave(true);
      return;
    } else {
      if (!cardholderFullName) return setIsLoading(false);
      if (ownerFullName?.toLowerCase() !== cardholderFullName?.toLowerCase()) {
        setToastDetail({
          type: "error",
          title: t("CreditCardUpdateModal-updateFailureTitle"),
          message: t("CreditCardUpdateModal-updateFailureDesc"),
        });
        setModalState(false);
        setIsLoading(false);
        setIsUpdated(!isUpdated);
        setDisableSave(true);
        reset();
        return;
      } else {
        if (errors.expirationDate && !result?.expirationDate)
          return setIsLoading(false);
        if (isValidExpireDate()) {
          let policyDetails = {
            cardHolderName: `${result?.firstName} ${result?.lastName}`,
            paymentProfileId: data?.paymentProfileId,
            cardExpiry: result?.expirationDate?.replace("/", ""),
            blockCode: selectedPolicy[0]?.blockCode,
          };
          await UpdatePaymentProfile(policyDetails)
            .then(() => {
              setToastDetail({
                type: "success",
                title: t("CreditCardUpdateModal-updateSuccessTitle"),
                message: t("CreditCardUpdateModal-updateSuccessDesc"),
              });
              setModalState(false);
              setIsLoading(false);
              setDisableSave(true);
              setIsUpdated(!isUpdated);
            })
            .catch((error) => {
              console.error(
                "Server refused to connect, please try again later.",
                error
              );
              setToastDetail({
                type: "error",
                title: t("CreditCardUpdateModal-updateFailureTitle"),
                message: t("CreditCardUpdateModal-updateFailureDesc"),
              });
              setModalState(false);
              setIsLoading(false);
              setIsUpdated(!isUpdated);
            });
        } else {
          setDisableSave(true);
          setIsLoading(false);
          setErrorMessage(t("CreditCardUpdateModal-expDateInvalidMessage"));
        }
      }
    }
  }

  useEffect(() => {
    setValue("expirationDate", data?.expDate);
  }, [data]);

  return (
    <>
      <Dialog
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 top-[20%] z-20 overflow-y-auto"
        open={modalState}
        onClose={() => null}
      >
        <DialogBackdrop />
        <Dialog.Panel className="ui-default-dialog md:max-w-xl">
          <Dialog.Title
            as="h2"
            className="dialog_label2 font-body text-xl font-bold text-left"
          >
            {t("CreditCardUpdateModal-title")}
            <button
              className="ui-default-dialog-close"
              onClick={() => {
                setModalState(false);
                setErrorMessage(undefined);
                setDisableSave(true);
                reset();
              }}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
          </Dialog.Title>
          <hr />
          <EverlakeLoadingOverlay isLoading={isLoading}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div>
                  <label id={`${uuid}-firstName-label`}>
                    {t("CreditCardUpdateModal-label-1")}
                  </label>
                  <input
                    id={`${uuid}-firstName`}
                    className="w-full"
                    type="text"
                    defaultValue={data?.ownerName?.split(" ")[0]?.trim()}
                    {...register("firstName", {
                      required: t("CreditCardUpdateModal-label-1-error"),
                      pattern: {
                        value: /^[^\d]*$/,
                        message: t("CreditCardUpdateModal-label-format-error"),
                      },
                    })}
                    aria-describedby={`${uuid}-firstName-error`}
                    aria-labelledby={`${uuid}-firstName-label`}
                    onChange={(e) => {
                      if (!errors.expirationDate) setDisableSave(false);
                      setValue("expirationDate", data?.expDate);
                    }}
                  />
                  {errors.firstName && (
                    <FormInputError
                      id={`${uuid}-firstName-error`}
                      errorMessage={errors.firstName.message}
                    />
                  )}
                </div>
                <div className="mt-4">
                  <label id={`${uuid}-lastName-label`}>
                    {t("CreditCardUpdateModal-label-2")}
                  </label>
                  <input
                    id={`${uuid}-lastName`}
                    className="w-full"
                    type="text"
                    defaultValue={data?.ownerName
                      ?.split(" ")
                      [data?.ownerName?.split(" ").length - 1]?.trim()}
                    {...register("lastName", {
                      required: t("CreditCardUpdateModal-label-2-error"),
                      pattern: {
                        value: /^[^\d]*$/,
                        message: t("CreditCardUpdateModal-label-format-error"),
                      },
                    })}
                    aria-describedby={`${uuid}-lastName-error`}
                    aria-labelledby={`${uuid}-lastName-label`}
                    onChange={(e) => {
                      if (!errors.expirationDate) setDisableSave(false);
                      setValue("expirationDate", data?.expDate);
                    }}
                  />
                  {errors.lastName && (
                    <FormInputError
                      id={`${uuid}-lastName-error`}
                      errorMessage={errors.lastName.message}
                    />
                  )}
                </div>

                <div className="mt-4">
                  <label id={`${uuid}-cardNumber-label`}>
                    {t("CreditCardUpdateModal-label-3")}
                  </label>
                  <input
                    id={`${uuid}-cardNumber`}
                    disabled={true}
                    className="w-full"
                    type="text"
                    defaultValue={data?.accountNumber}
                  />
                </div>

                <div className="mt-4">
                  <div className="flex items-center">
                    <label id={`${uuid}-cardNumber-label`}>
                      {t("CreditCardUpdateModal-label-4")}
                    </label>
                    <Tooltip
                      xMargin="mx-0"
                      content={
                        <>{t("CreditCardUpdateModal-label-4-tooltip")}</>
                      }
                    />
                  </div>
                  <input
                    id={`${uuid}-cardNumber`}
                    disabled={true}
                    className="w-full"
                    defaultValue="***"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 grid-flow-row gap-4 mt-4 items-center">
                  <div>
                    <label id={`expirationDate-label`}>
                      {t("CreditCardUpdateModal-label-5")}
                    </label>
                    <MaskedExpirationDateInput
                      name="expirationDate"
                      describedby={`expirationDate-label`}
                      register={register}
                      setValue={setValue}
                      defaultValue={data?.expDate}
                      setState={setDisableSave}
                      errorMessage={t(
                        "CreditCardUpdateModal-expDateErrorMessage"
                      )}
                    />
                    {errors.expirationDate && (
                      <FormInputError
                        id={`expirationDate-error`}
                        errorMessage={errors.expirationDate.message}
                      />
                    )}
                    {errorMessage && (
                      <FormInputError
                        id={`expirationDate-error`}
                        errorMessage={errorMessage}
                      />
                    )}
                  </div>
                </div>
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary min-w-[140px] mt-4"
                    aria-label="cancel update credit card expiration date"
                    onClick={() => {
                      setErrorMessage(undefined);
                      setModalState(false);
                      setDisableSave(true);
                      reset();
                    }}
                  >
                    {t("CreditCardUpdateModal-buttonCancel")}
                  </button>
                  <button
                    id="ID_submitPaymentMethod"
                    className="ui-btn-primary min-w-[140px] mt-4"
                    aria-label="save update credit card expiration date"
                    disabled={disableSave}
                    onClick={() => {
                      onSubmit();
                    }}
                  >
                    {t("CreditCardUpdateModal-buttonSave")}
                  </button>
                </div>
              </div>
            </form>
          </EverlakeLoadingOverlay>
        </Dialog.Panel>
      </Dialog>
      <PaymentProfileConfirmationModel
        state={openDialog}
        setState={setOpenDialog}
        title={title}
        desc={message}
        setIsUpdated={setOpenDialog}
        isUpdated={openDialog}
      />
    </>
  );
};

export default UpdateCCModal;
